import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { useFieldOptional } from './field-context';
import { IconEye, IconEyeOff } from './icons';
import { cn, disabledStyles, focusRing } from './utils';

export const controlBaseStyles = cn(
  'w-full rounded-md border border-border-control bg-surface text-content',
  'placeholder:text-content-subtle',
  'transition-colors duration-100',
  focusRing,
  disabledStyles,
  'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-danger',
);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly leadingIcon?: ReactNode;
  readonly trailingSlot?: ReactNode;
  readonly inputSize?: 'sm' | 'md';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, leadingIcon, trailingSlot, inputSize = 'md', ...props },
  ref,
) {
  const field = useFieldOptional();

  return (
    <div className="relative flex items-center">
      {leadingIcon ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 flex text-content-subtle"
        >
          {leadingIcon}
        </span>
      ) : null}
      <input
        ref={ref}
        {...field?.controlProps}
        {...props}
        className={cn(
          controlBaseStyles,
          inputSize === 'md' ? 'h-[2.375rem] px-3 text-base' : 'h-8 px-2.5 text-sm',
          leadingIcon && 'pl-9',
          trailingSlot && 'pr-10',
          className,
        )}
      />
      {trailingSlot ? (
        <span className="absolute right-1 flex items-center">{trailingSlot}</span>
      ) : null}
    </div>
  );
});

/**
 * Campo de senha com alternância de visibilidade.
 *
 * ACESSIBILIDADE: o nome acessível do botão MUDA conforme o estado
 * ("Mostrar senha" ↔ "Ocultar senha") e o estado corrente também vai em
 * `aria-pressed`. Sem isso quem usa leitor de tela não descobre se a senha está
 * exposta na tela — num posto de trabalho compartilhado isso é risco real.
 */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputProps, 'type'>>(
  function PasswordInput({ className, ...props }, ref) {
    const [visible, setVisible] = useState(false);

    return (
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cn('font-mono tracking-tight', className)}
        trailingSlot={
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-pressed={visible}
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded text-content-subtle',
              'hover:bg-surface-sunken hover:text-content',
              focusRing,
            )}
          >
            {visible ? <IconEyeOff size={16} /> : <IconEye size={16} />}
          </button>
        }
        {...props}
      />
    );
  },
);

/** Campo numérico com alinhamento tabular — permite comparar valores em coluna. */
export const NumberInput = forwardRef<HTMLInputElement, InputProps>(function NumberInput(
  { className, ...props },
  ref,
) {
  return (
    <Input
      ref={ref}
      type="number"
      inputMode="numeric"
      className={cn('qc-tabular', className)}
      {...props}
    />
  );
});
